import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Save, MapPin, Building2, Calendar, Globe, Lock, Archive, AlertTriangle,
  Loader2, CheckCircle2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { HudCard, HudButton } from '../hud';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

const STATUS_OPTIONS = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'en_cours',   label: 'En cours' },
  { value: 'publie',     label: 'Publié' },
  { value: 'archive',    label: 'Archivé' },
];

const CUSTOM_DATES = [
  { key: 'date_depot_pc',           label: 'Dépôt du permis de construire' },
  { key: 'date_ouverture_chantier', label: 'Ouverture du chantier' },
  { key: 'date_commercialisation',  label: 'Début de la commercialisation' },
  { key: 'date_livraison',          label: 'Livraison prévisionnelle' },
];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const LabeledInput = ({ label, value, onChange, placeholder, type = 'text', testid, disabled, hint }) => (
  <div>
    <label className="hud-eyebrow block mb-1.5">{label}</label>
    <Input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      disabled={disabled}
      className="h-9"
    />
    {hint && <p className="mt-1 text-[11px]" style={{ color: 'var(--c-text-faint)' }}>{hint}</p>}
  </div>
);

const LabeledTextarea = ({ label, value, onChange, placeholder, rows = 3, testid }) => (
  <div>
    <label className="hud-eyebrow block mb-1.5">{label}</label>
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testid}
      className="w-full rounded-lg p-3 text-[13px] outline-none resize-y"
      style={{
        background: 'var(--c-surface-2)',
        border: '1px solid var(--c-border)',
        color: 'var(--c-text)',
      }}
    />
  </div>
);

const ProjectSettingsSection = ({ project, projectId, onSaved }) => {
  const [form, setForm] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Calendar block ref + focused-date highlight (driven by ?focus= URL param)
  const calendarRef = useRef(null);
  const [focusedDateKey, setFocusedDateKey] = useState(null);

  useEffect(() => {
    if (!project) return;
    // V14.2 / Sprint C Phase 2 — building_count, floor_count_estimate,
    // is_new_construction et project_type_code sont désormais gérés dans
    // Paramétrage projet → Caractéristiques. Le catalogue Styles/Matériaux
    // est dans Paramétrage projet → Catalogue.
    const initial = {
      name: project.name || '',
      description: project.description || '',
      address: project.address || '',
      coords: project.coords || null,
      status: project.status || 'en_attente',
      is_public: !!project.is_public,
      general_info: project.general_info || {},
      created_at: project.created_at,
      updated_at: project.updated_at,
    };
    setForm(initial);
    setOriginalForm(initial);
  }, [project]);

  // Read ?focus=<date_key> from URL to highlight + scroll + autofocus the field
  useEffect(() => {
    if (!form) return;
    let key;
    try {
      const params = new URLSearchParams(window.location.search);
      key = params.get('focus');
    } catch { key = null; }
    const validKeys = CUSTOM_DATES.map((d) => d.key);
    if (!key || !validKeys.includes(key)) return;
    setFocusedDateKey(key);
    // Wait next frame so the card is in the DOM
    const t = setTimeout(() => {
      if (calendarRef.current) {
        calendarRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      const input = document.querySelector(`[data-testid="settings-date-${key}"]`);
      if (input) {
        try { input.focus({ preventScroll: true }); } catch { input.focus(); }
        try { input.showPicker?.(); } catch { /* showPicker not always supported */ }
      }
      // Cleanup query param (keep ?section=settings)
      const url = new URL(window.location.href);
      url.searchParams.delete('focus');
      window.history.replaceState({}, '', url.toString());
    }, 220);
    // Auto-clear highlight after a few seconds
    const t2 = setTimeout(() => setFocusedDateKey(null), 2600);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [form]);

  const setField = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const setGeneralInfoField = useCallback((key, value) => {
    setForm((f) => ({ ...f, general_info: { ...(f.general_info || {}), [key]: value } }));
  }, []);

  const isDirty = JSON.stringify(form) !== JSON.stringify(originalForm);

  const geocode = async () => {
    if (!form?.address?.trim()) {
      toast.error('Renseignez une adresse d\'abord');
      return;
    }
    setGeocoding(true);
    try {
      const r = await axios.post(`${API}/geocode`, { address: form.address }, opts);
      if (!r.data?.found) {
        toast.error('Adresse introuvable via OpenStreetMap');
      } else {
        setField('coords', r.data.coords);
        toast.success(`Géocodé : ${r.data.display_name}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du géocodage');
    } finally {
      setGeocoding(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build clean payload: send only non-empty changeable fields.
      // V14.2 / Sprint C : caractéristiques techniques sont gérées ailleurs.
      const payload = {
        name: form.name?.trim(),
        description: form.description?.trim() || '',
        address: form.address?.trim() || '',
        coords: form.coords,
        status: form.status,
        is_public: !!form.is_public,
        general_info: form.general_info || {},
      };
      const r = await axios.put(`${API}/projects/${projectId}`, payload, opts);
      const next = {
        ...form,
        ...r.data,
        general_info: r.data.general_info || form.general_info || {},
      };
      setForm(next);
      setOriginalForm(next);
      toast.success('Projet mis à jour');
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm('Archiver ce projet ? Il sera masqué des vues actives mais conservé.')) return;
    setArchiving(true);
    try {
      await axios.put(`${API}/projects/${projectId}`, { status: 'archive' }, opts);
      toast.success('Projet archivé');
      setField('status', 'archive');
      setOriginalForm((f) => ({ ...f, status: 'archive' }));
      onSaved?.({ ...project, status: 'archive' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'archivage');
    } finally {
      setArchiving(false);
    }
  };

  if (!form) {
    return (
      <div className="flex items-center gap-2 text-[13px] py-12 justify-center" style={{ color: 'var(--c-text-2)' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="hud-stagger space-y-5" data-testid="project-settings-section">
      {/* ============================================================ */}
      {/* Bloc 1 — Identité                                              */}
      {/* ============================================================ */}
      <HudCard variant="glass" bracket>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Identité</span>
            <p
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
            >
              Informations générales
            </p>
            <p className="text-[12.5px] mt-1" style={{ color: 'var(--c-text-2)' }}>
              Nom, descriptif, localisation et statut du projet immobilier.
            </p>
          </div>
          <Building2 className="w-5 h-5 shrink-0" style={{ color: 'var(--c-gold)' }} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LabeledInput
            label="Nom du projet"
            value={form.name}
            onChange={(v) => setField('name', v)}
            placeholder="ex. Menara"
            testid="settings-input-name"
          />
          <div>
            <label className="hud-eyebrow block mb-1.5">Statut</label>
            <select
              value={form.status}
              onChange={(e) => setField('status', e.target.value)}
              data-testid="settings-select-status"
              className="w-full h-9 rounded-md px-3 text-[13px] outline-none"
              style={{
                background: 'var(--c-surface-2)',
                border: '1px solid var(--c-border)',
                color: 'var(--c-text)',
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <LabeledTextarea
            label="Description"
            value={form.description}
            onChange={(v) => setField('description', v)}
            placeholder="Pitch commercial, contexte, particularités du programme…"
            rows={3}
            testid="settings-input-description"
          />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <LabeledInput
            label="Adresse"
            value={form.address}
            onChange={(v) => setField('address', v)}
            placeholder="ex. Avenue Mohammed VI, Marrakech, Maroc"
            testid="settings-input-address"
            hint={form.coords ? `Coordonnées : ${form.coords[0].toFixed(5)}, ${form.coords[1].toFixed(5)}` : 'Aucune coordonnée géocodée'}
          />
          <HudButton
            variant="secondary"
            size="sm"
            onClick={geocode}
            disabled={geocoding || !form.address?.trim()}
            leftIcon={geocoding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
            data-testid="settings-geocode-btn"
          >
            {geocoding ? 'Géocodage…' : 'Géocoder l\'adresse'}
          </HudButton>
        </div>

        <label className="mt-5 flex items-start gap-2.5 cursor-pointer" data-testid="settings-toggle-public">
          <input
            type="checkbox"
            checked={!!form.is_public}
            onChange={(e) => setField('is_public', e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#D9A05B] cursor-pointer"
          />
          <span>
            <span className="flex items-center gap-1.5 text-[13px] font-medium" style={{ color: 'var(--c-text)' }}>
              {form.is_public ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
              Visible publiquement sur la landing page
            </span>
            <span className="block text-[11.5px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
              Quand activé, ce projet apparaît dans le carrousel public de plan-prep-canvas.preview.emergentagent.com (vue dégradée, coords + nom uniquement).
            </span>
          </span>
        </label>
      </HudCard>

      {/* ============================================================ */}
      {/* Bloc 2 — Caractéristiques techniques [MIGRATED]                */}
      {/* Bloc déplacé dans Paramétrage projet → Caractéristiques        */}
      {/* (Sprint C Phase 2 — Feb 2026)                                  */}
      {/* ============================================================ */}

      {/* ============================================================ */}
      {/* Bloc 3 — Dates                                                 */}
      {/* ============================================================ */}
      <HudCard variant="glass" ref={calendarRef}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <span className="hud-eyebrow hud-eyebrow-gold">Calendrier</span>
            <p
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 18 }}
            >
              Jalons & dates clés
            </p>
            <p className="text-[12.5px] mt-1" style={{ color: 'var(--c-text-2)' }}>
              Dates prévisionnelles utilisées par l'Agenda commercial (à venir).
            </p>
          </div>
          <Calendar className="w-5 h-5 shrink-0" style={{ color: 'var(--c-gold)' }} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CUSTOM_DATES.map((d) => {
            const isFocused = focusedDateKey === d.key;
            return (
              <div
                key={d.key}
                className="transition-all duration-300 rounded-lg"
                style={{
                  padding: isFocused ? 8 : 0,
                  margin: isFocused ? -8 : 0,
                  background: isFocused ? 'var(--c-gold-soft)' : 'transparent',
                  boxShadow: isFocused ? '0 0 0 1px var(--c-border-accent), 0 0 18px rgba(217,160,91,0.30)' : 'none',
                }}
                data-testid={`settings-date-wrapper-${d.key}`}
              >
                <LabeledInput
                  label={d.label}
                  value={form.general_info?.[d.key] || ''}
                  onChange={(v) => setGeneralInfoField(d.key, v)}
                  placeholder="JJ/MM/AAAA"
                  type="date"
                  testid={`settings-date-${d.key}`}
                />
              </div>
            );
          })}
        </div>

        <div
          className="mt-4 rounded-lg p-3 text-[11.5px] flex items-center justify-between gap-3 flex-wrap"
          style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}
        >
          <div>
            <span style={{ color: 'var(--c-text-muted)' }}>Créé le</span>{' '}
            <span style={{ color: 'var(--c-text)' }}>{fmtDateTime(form.created_at)}</span>
          </div>
          <div>
            <span style={{ color: 'var(--c-text-muted)' }}>Dernière modification</span>{' '}
            <span style={{ color: 'var(--c-text)' }}>{fmtDateTime(form.updated_at)}</span>
          </div>
        </div>
      </HudCard>

      {/* ============================================================ */}
      {/* Save bar                                                       */}
      {/* ============================================================ */}
      <div
        className="sticky bottom-3 z-10 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap"
        style={{
          background: 'rgba(14,17,22,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--c-border)',
          boxShadow: '0 12px 32px -12px rgba(0,0,0,0.6)',
        }}
        data-testid="settings-save-bar"
      >
        <div className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
          {isDirty ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
              <span>Modifications non enregistrées</span>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#86EFAC' }} />
              <span>À jour</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <HudButton
            variant="ghost"
            size="sm"
            onClick={() => setForm(originalForm)}
            disabled={!isDirty || saving}
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
            data-testid="settings-reset-btn"
          >
            Annuler
          </HudButton>
          <HudButton
            variant="primary"
            size="sm"
            onClick={save}
            disabled={!isDirty || saving}
            leftIcon={saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            data-testid="settings-save-btn"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </HudButton>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Bloc 4 — Zone d'archivage                                      */}
      {/* ============================================================ */}
      {form.status !== 'archive' && (
        <HudCard variant="glass" className="border-amber-500/30">
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(234,179,8,0.14)', border: '1px solid rgba(234,179,8,0.32)' }}
            >
              <Archive className="w-4 h-4" style={{ color: '#FDE68A' }} />
            </div>
            <div className="flex-1">
              <span className="hud-eyebrow" style={{ color: '#FDE68A' }}>Zone d'archivage</span>
              <p
                className="mt-0.5 font-semibold tracking-tight"
                style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 15 }}
              >
                Archiver ce projet
              </p>
              <p className="text-[12px] mt-1 max-w-xl" style={{ color: 'var(--c-text-2)' }}>
                Le projet sera masqué des listes actives et de la carte Hub, mais l'ensemble des données (lots, rendus, matériauthèque, contacts liés) sera conservé. Réversible à tout moment en repassant le statut sur « En cours ».
              </p>
            </div>
            <HudButton
              variant="secondary"
              size="sm"
              onClick={archive}
              disabled={archiving}
              leftIcon={archiving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
              data-testid="settings-archive-btn"
              className="shrink-0"
            >
              {archiving ? 'Archivage…' : 'Archiver le projet'}
            </HudButton>
          </div>
        </HudCard>
      )}

      {form.status === 'archive' && (
        <HudCard variant="glass">
          <div className="flex items-center gap-3">
            <Archive className="w-4 h-4" style={{ color: 'var(--c-text-muted)' }} />
            <p className="text-[13px]" style={{ color: 'var(--c-text-2)' }}>
              Ce projet est <strong style={{ color: 'var(--c-text)' }}>archivé</strong>. Passez le statut sur « En cours » et cliquez sur Enregistrer pour le réactiver.
            </p>
          </div>
        </HudCard>
      )}

      {/* ============================================================ */}
      {/* Bloc 5 — Catalogue [MIGRATED]                                  */}
      {/* Bloc déplacé dans Paramétrage projet → Catalogue               */}
      {/* (Sprint C Phase 2 — Feb 2026)                                  */}
      {/* ============================================================ */}
    </div>
  );
};

export default ProjectSettingsSection;
